import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

import _throttle from 'lodash/throttle';

type PopoverState = {
  isOpen: boolean;
  pos: { x: number; y: number; width: number };
};

const defaultOpts = {
  repositionOnResize: false,
};

const getPos = (ref: React.RefObject<HTMLButtonElement>) => {
  const viewportOffset = ref.current?.getBoundingClientRect();

  return {
    x: (viewportOffset?.left || 0) + window.scrollX,
    y: (viewportOffset?.top || 0) + window.scrollY,
    width: ref.current?.offsetWidth || 0,
  };
};

export default function usePopoverState(opts = defaultOpts) {
  const anchorElementRef = useRef<HTMLButtonElement>(null);
  const containerElementRef = useRef<HTMLDivElement>(null);

  const [{ isOpen, pos }, setPopoverState] = useState<PopoverState>({
    isOpen: false,
    pos: { x: 0, y: 0, width: 400 },
  });

  const updatePosition = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    setPopoverState((s) => ({ ...s, pos: getPos(anchorElementRef) }));
  }, [anchorElementRef]);

  const openPopover = () => {
    setPopoverState((s) => ({ ...s, isOpen: true }));
    updatePosition();
  };

  const closePopover = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    setPopoverState((s) => ({ ...s, isOpen: false }));
  }, []);

  useEffect(() => {
    if (isOpen) {
      // add backdrop to catch clicks and close the popover
      const backdrop = document.createElement('div');
      backdrop.addEventListener('click', () => closePopover());
      backdrop.classList.add('combobox-backdrop');
      backdrop.setAttribute('role', 'presentation');
      document.body.appendChild(backdrop);

      // listen for hotkeys when its open
      const hotkeys = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          closePopover();
        }
      };

      document.addEventListener('keydown', hotkeys);

      const resizeEvent = opts.repositionOnResize ? updatePosition : closePopover;

      const isMobile = /Mobi|Android/i.test(navigator.userAgent);

      const resizeListener = _throttle(resizeEvent, 100);

      window.addEventListener('resize', resizeListener);

      const scrollListener = (e: Event) => {
        // allow elements within the popover to scroll without triggering close or repositioning
        if (!containerElementRef.current?.contains(e.target as Node) && !isMobile) {
          resizeListener();
        }
      };
      document.addEventListener('scroll', scrollListener);

      // TODO determine if there is a way around this
      const temporaryCdsHijackFix = function (event: WheelEvent) {
        event.stopImmediatePropagation();
        // window.dispatchEvent(event);
      };
      document.addEventListener('wheel', temporaryCdsHijackFix, true);

      return () => {
        document.body.removeChild(backdrop);

        document.removeEventListener('keydown', hotkeys);
        window.removeEventListener('resize', resizeListener);
        document.removeEventListener('scroll', scrollListener);
        document.removeEventListener('wheel', temporaryCdsHijackFix, true);
      };
    }
    return () => undefined;
  }, [containerElementRef, isOpen, updatePosition, closePopover, opts]);

  return {
    anchorElementRef,
    containerElementRef,
    isOpen,
    pos,
    openPopover,
    closePopover,
  };
}
